import React from "react";
import { Box, Typography, Paper, Grid } from "@mui/material";
import TableSection from "../TableSection";
import { PieChart, BarChart } from "../Charts";
import { formatBRL, formatNumber, formatDays } from "../utils/formatters";

export default function CategoriasMarcasTab({
  salesByCategory,
  salesByBrand,
  catalogAge
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Vendas por categoria */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: '#1a237e' }}>
          Desempenho por Categoria
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: '#607d8b' }}>
          Análise detalhada do desempenho das principais categorias do catálogo.
        </Typography>
        
        {salesByCategory && salesByCategory.length > 0 ? (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TableSection
                showTitle={false}
                columns={[
                  { field: "categoryName", headerName: "Categoria" },
                  { field: "totalSales", headerName: "Vendas", format: (v) => formatNumber(v, 0) },
                  { field: "totalRevenue", headerName: "Receita", format: (v) => formatBRL(v) }
                ]}
                rows={salesByCategory || []}
                emptyMessage="Nenhum dado disponível"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ width: '100%', height: 300 }}>
                <PieChart
                  series={[{ 
                    data: salesByCategory.map((c) => ({ 
                      id: c.categoryName, 
                      value: c.totalRevenue, 
                      label: c.categoryName 
                    })),
                    innerRadius: 30,
                    outerRadius: 100,
                    paddingAngle: 1,
                    cornerRadius: 3
                  }]}
                  width={undefined}
                  height={300}
                  slotProps={{
                    legend: {
                      direction: 'column',
                      position: { vertical: 'middle', horizontal: 'right' },
                      padding: 0
                    }
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <Typography variant="body1" color="text.secondary">Nenhum dado disponível para o período selecionado</Typography>
          </Box>
        )}
      </Paper>
      
      {/* Vendas por marca */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: '#1a237e' }}>
          Desempenho por Marca
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: '#607d8b' }}>
          Total de vendas, unidades e receita agrupados por marca.
        </Typography>
        
        {salesByBrand && salesByBrand.length > 0 ? (
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <TableSection
                showTitle={false}
                columns={[
                  { field: "brandName", headerName: "Marca" }, 
                  { field: "totalSales", headerName: "Vendas", format: (v) => formatNumber(v, 0) }, 
                  { field: "totalUnits", headerName: "Unidades", format: (v) => formatNumber(v, 0) }, 
                  { field: "totalRevenue", headerName: "Receita", format: (v) => formatBRL(v) }
                ]}
                rows={salesByBrand || []}
                emptyMessage="Nenhum dado disponível"
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <Box sx={{ width: '100%', height: 300 }}>
                <BarChart
                  xAxis={[{ 
                    scaleType: "band", 
                    data: salesByBrand.slice(0, 5).map((b) => b.brandName),
                    label: 'Marcas (Top 5)'
                  }]}
                  series={[{ 
                    data: salesByBrand.slice(0, 5).map((b) => b.totalSales), 
                    label: "Vendas",
                    color: '#1a237e'
                  }]}
                  width={undefined}
                  height={280}
                />
              </Box>
            </Grid>
          </Grid>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <Typography variant="body1" color="text.secondary">Nenhum dado disponível para o período selecionado</Typography>
          </Box>
        )}
      </Paper>
      
      {/* Idade do catálogo */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: '#1a237e' }}>
          Idade do Catálogo
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: '#607d8b' }}>
          Idade média, produto mais novo e mais antigo no catálogo.
        </Typography>
        
        {catalogAge && catalogAge[0] ? (
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, textAlign: 'center' }}>Idade Média</Typography>                <Typography variant="h5" fontWeight={600} color="primary" align="center">
                  {formatNumber(catalogAge[0].averageAgeInDays, 1)} dias
                </Typography>
                <Typography variant="caption" color="text.secondary" align="center" sx={{ display: 'block' }}>
                  ({formatNumber(catalogAge[0].averageAgeInMonths, 1)} meses)
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, textAlign: 'center' }}>Produto Mais Antigo</Typography>                <Typography variant="h5" fontWeight={600} color="warning.main" align="center">
                  {formatNumber(catalogAge[0].oldestProductDays, 1)} dias
                </Typography>
                <Typography variant="caption" color="text.secondary" align="center" sx={{ display: 'block' }}>
                  ({formatNumber(catalogAge[0].oldestProductMonths, 1)} meses)
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, textAlign: 'center' }}>Produto Mais Novo</Typography>                <Typography variant="h5" fontWeight={600} color="success.main" align="center">
                  {formatNumber(catalogAge[0].newestProductDays, 1)} dias
                </Typography>
                <Typography variant="caption" color="text.secondary" align="center" sx={{ display: 'block' }}>
                  ({formatNumber(catalogAge[0].newestProductMonths, 1)} meses)
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ mt: 2, p: 2, borderTop: '1px solid #eee' }}>
                <Typography variant="body2" color="text.secondary">
                  Análise baseada em um total de {catalogAge[0].totalProducts} produtos no catálogo {catalogAge[0].name ? `para ${catalogAge[0].name}` : ''}.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <Typography variant="body1" color="text.secondary">Nenhum dado disponível para o período selecionado</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
